import { useState, useEffect } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import cogoToast from "cogo-toast";
import { getLeadMagnet, postLeadMagnetResponseForm } from "../api/utils";
import useFetch from "../api/hooks/useFetch";

import FiveSignsYouNeedCustomBusinessSoftwareStyles from "./styles/leadmagents/FiveSignsYouNeedCustomSoftware";
const FiveSignsYouNeedCustomBusinessSoftware = () => {
  const slides = [
    // Slide 0: Hero
    {
      id: "hero",
      content: (
        <div className="lead-magnet-hero">
          <div className="hero-content">
            <div className="hero-badge">
              <span className="badge-icon">📊</span>
              <span className="badge-text">FREE GUIDE</span>
            </div>

            <h1 className="hero-title">
              <span className="highlight">5 Signs</span> You Need Custom
              Business Software
              <br />
              <span className="hero-subtitle">
                (Not Another Off-Shelf Tool)
              </span>
            </h1>

            <p className="hero-description">
              Is your current software holding your business back? Discover how
              to identify when it's time to stop compromising and build a custom
              solution that fits YOUR workflow perfectly.
            </p>

            <div className="hero-stats">
              <div className="stat-item">
                <div className="stat-value">68%</div>
                <div className="stat-label">Average Efficiency Gain</div>
              </div>
              <div className="stat-item">
                <div className="stat-value">8-18</div>
                <div className="stat-label">Months to ROI</div>
              </div>
              <div className="stat-item">
                <div className="stat-value">500+</div>
                <div className="stat-label">Businesses Transformed</div>
              </div>
            </div>
          </div>
        </div>
      ),
    },

    // Slide 1: Introduction
    {
      id: "intro",
      content: (
        <div className="content-slide">
          <div className="slide-header">
            <div className="slide-number">📋</div>
            <h2 className="slide-title">
              Are You Fighting Your Software Instead of Using It?
            </h2>
          </div>

          <div className="slide-content">
            <p className="slide-text">
              If you're a business owner or decision-maker, you've probably felt
              this frustration: the software that's supposed to make your life
              easier is actually making it harder.
            </p>

            <div className="info-card">
              <div className="info-card-header">
                <span className="info-icon">💡</span>
                <h3>Here's the truth:</h3>
              </div>
              <p className="info-card-text">
                Off-the-shelf software works for average businesses doing
                average things. But if you want to compete, scale, or operate
                differently than everyone else, you need software that's built
                for <strong>YOU</strong>.
              </p>
            </div>

            <p className="slide-text">
              You're constantly working around limitations, paying for features
              you don't use, and watching competitors pull ahead while you're
              stuck with "good enough" solutions.
            </p>

            <div className="cta-box">
              <div className="cta-icon">🎯</div>
              <div className="cta-content">
                <h4>This Guide Will Help You:</h4>
                <ul className="cta-list">
                  <li>
                    Identify the 5 clear signs it's time for custom software
                  </li>
                  <li>Calculate your potential ROI and efficiency gains</li>
                  <li>
                    Make an informed decision about custom vs off-the-shelf
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      ),
    },

    // Slide 2: Sign #1
    {
      id: "sign1",
      content: (
        <div className="content-slide">
          <div className="slide-header">
            <div className="slide-number">1</div>
            <h2 className="slide-title">
              Your Team Has Created "Workarounds" For Everything
            </h2>
          </div>

          <div className="slide-content">
            <div className="symptom-card">
              <div className="symptom-header">
                <span className="symptom-icon">🔄</span>
                <h3>What This Looks Like:</h3>
              </div>
              <ul className="symptom-list">
                <li>
                  <span className="list-icon">📊</span>
                  <span>
                    Employees maintain separate Excel sheets because the
                    software doesn't track what they need
                  </span>
                </li>
                <li>
                  <span className="list-icon">🔗</span>
                  <span>
                    Manual data entry between systems because they don't
                    integrate
                  </span>
                </li>
                <li>
                  <span className="list-icon">📝</span>
                  <span>
                    Sticky notes, email reminders, and side processes to
                    compensate for missing features
                  </span>
                </li>
                <li>
                  <span className="list-icon">🏢</span>
                  <span>
                    "That's just how we do it" has become the company motto
                  </span>
                </li>
              </ul>
            </div>

            <div className="impact-card">
              <h3>Why This Matters:</h3>
              <p className="impact-text">
                Every workaround costs time. Every manual process introduces
                errors. Every extra step slows you down.
              </p>
              <div className="stat-box">
                <span className="stat-number">15-20</span>
                <span className="stat-unit">Hours wasted per week</span>
              </div>
            </div>

            <div className="solution-card">
              <div className="solution-header">
                <span className="solution-icon">✨</span>
                <h3>The Custom Solution:</h3>
              </div>
              <p className="solution-text">
                Software built specifically for YOUR workflow eliminates
                workarounds by design. Every feature exists because YOU need it,
                not because it looked good in a demo.
              </p>
            </div>

            <div className="question-box">
              <h3 className="question-title">Ask Yourself:</h3>
              <ul className="question-list">
                <li>
                  How many hours per week does your team spend on manual
                  workarounds?
                </li>
                <li>
                  If you valued that time at $12/hour, what's the monthly cost?
                </li>
                <li>
                  Could custom business software eliminate 80% of these
                  workarounds?
                </li>
              </ul>
            </div>
          </div>
        </div>
      ),
    },

    // Slide 3: Sign #2
    {
      id: "sign2",
      content: (
        <div className="content-slide">
          <div className="slide-header">
            <div className="slide-number">2</div>
            <h2 className="slide-title">
              Your Software Costs Keep Increasing (With No Added Value)
            </h2>
          </div>

          <div className="slide-content">
            <div className="symptom-card">
              <div className="symptom-header">
                <span className="symptom-icon">💰</span>
                <h3>What This Looks Like:</h3>
              </div>
              <ul className="symptom-list">
                <li>
                  <span className="list-icon">📈</span>
                  <span>Started at $60/month, now paying $300/month</span>
                </li>
                <li>
                  <span className="list-icon">⚡</span>
                  <span>
                    Every new feature requires an "enterprise upgrade"
                  </span>
                </li>
                <li>
                  <span className="list-icon">👥</span>
                  <span>Per-user pricing means growth = higher bills</span>
                </li>
                <li>
                  <span className="list-icon">➕</span>
                  <span>
                    Add-ons, integrations, and "premium features"
                    nickel-and-diming you
                  </span>
                </li>
                <li>
                  <span className="list-icon">⚠️</span>
                  <span>
                    Annual price increases "to maintain service quality"
                  </span>
                </li>
              </ul>
            </div>

            <div className="impact-card">
              <h3>Why This Matters:</h3>
              <p className="impact-text">
                Off-the-shelf software has one goal: maximize recurring revenue.
                They know switching is painful, so they slowly increase prices
                year after year.
              </p>
              <div className="stat-box">
                <span className="stat-number">15-25%</span>
                <span className="stat-unit">Annual price increases</span>
              </div>
            </div>

            <div className="solution-card">
              <div className="solution-header">
                <span className="solution-icon">👑</span>
                <h3>The Custom Solution:</h3>
              </div>
              <p className="solution-text">
                Custom software is an investment, not a subscription. Build it
                once, own it forever. No per-user fees. No surprise price
                increases. No feature hostages.
              </p>
            </div>
          </div>
        </div>
      ),
    },

    // Slide 4: Sign #3
    {
      id: "sign3",
      content: (
        <div className="content-slide">
          <div className="slide-header">
            <div className="slide-number">3</div>
            <h2 className="slide-title">
              You Can't Get The Reports/Insights You Actually Need
            </h2>
          </div>

          <div className="slide-content">
            <div className="symptom-card">
              <div className="symptom-header">
                <span className="symptom-icon">📊</span>
                <h3>What This Looks Like:</h3>
              </div>
              <ul className="symptom-list">
                <li>
                  <span className="list-icon">❌</span>
                  <span>"The system doesn't generate that report"</span>
                </li>
                <li>
                  <span className="list-icon">📤</span>
                  <span>Exporting data to Excel to create basic charts</span>
                </li>
                <li>
                  <span className="list-icon">🎯</span>
                  <span>Can't track metrics that matter to YOUR business</span>
                </li>
                <li>
                  <span className="list-icon">📱</span>
                  <span>
                    Generic dashboards showing data you don't care about
                  </span>
                </li>
                <li>
                  <span className="list-icon">💸</span>
                  <span>
                    Paying extra for "custom reporting" that's still limited
                  </span>
                </li>
              </ul>
            </div>

            <div className="impact-card">
              <h3>Why This Matters:</h3>
              <p className="impact-text">
                You can't manage what you can't measure. If your software can't
                tell you what's really happening in your business, you're flying
                blind.
              </p>
              <div className="stat-box">
                <span className="stat-number">40%</span>
                <span className="stat-unit">
                  Faster decisions with right data
                </span>
              </div>
            </div>

            <div className="solution-card">
              <div className="solution-header">
                <span className="solution-icon">🚀</span>
                <h3>The Custom Solution:</h3>
              </div>
              <p className="solution-text">
                Build exactly the dashboards, reports, and analytics YOU need.
                Track YOUR metrics. See YOUR data, YOUR way, in real-time.
              </p>
            </div>
          </div>
        </div>
      ),
    },

    // Slide 5: Sign #4
    {
      id: "sign4",
      content: (
        <div className="content-slide">
          <div className="slide-header">
            <div className="slide-number">4</div>
            <h2 className="slide-title">
              Your Business is Growing But Your Software Isn't
            </h2>
          </div>

          <div className="slide-content">
            <div className="symptom-card">
              <div className="symptom-header">
                <span className="symptom-icon">📈</span>
                <h3>What This Looks Like:</h3>
              </div>
              <ul className="symptom-list">
                <li>
                  <span className="list-icon">⛔</span>
                  <span>
                    Hit user limits, transaction caps, or storage restrictions
                  </span>
                </li>
                <li>
                  <span className="list-icon">💎</span>
                  <span>"Upgrade to enterprise" = 3x the price</span>
                </li>
                <li>
                  <span className="list-icon">⌛</span>
                  <span>
                    Features you need are "coming in a future update" (that
                    never comes)
                  </span>
                </li>
                <li>
                  <span className="list-icon">🏢</span>
                  <span>
                    Can't handle new locations, departments, or business models
                  </span>
                </li>
                <li>
                  <span className="list-icon">🐌</span>
                  <span>System slows down as data grows</span>
                </li>
              </ul>
            </div>

            <div className="impact-card">
              <h3>Why This Matters:</h3>
              <p className="impact-text">
                Your software should enable growth, not restrict it. When
                scaling your business means fighting your software, you've got
                the wrong software.
              </p>
              <div className="stat-box">
                <span className="stat-number">2-3x</span>
                <span className="stat-unit">Higher costs for "enterprise"</span>
              </div>
            </div>

            <div className="solution-card">
              <div className="solution-header">
                <span className="solution-icon">⚡</span>
                <h3>The Custom Solution:</h3>
              </div>
              <p className="solution-text">
                Software that scales with you because it's designed for YOUR
                growth trajectory. Add users, locations, features without
                arbitrary limits or extortion-level "enterprise" pricing.
              </p>
            </div>
          </div>
        </div>
      ),
    },

    // Slide 6: Sign #5
    {
      id: "sign5",
      content: (
        <div className="content-slide">
          <div className="slide-header">
            <div className="slide-number">5</div>
            <h2 className="slide-title">
              Your Competitors Use The Same Software (So What's Your Edge?)
            </h2>
          </div>

          <div className="slide-content">
            <div className="symptom-card">
              <div className="symptom-header">
                <span className="symptom-icon">⚔️</span>
                <h3>What This Looks Like:</h3>
              </div>
              <ul className="symptom-list">
                <li>
                  <span className="list-icon">🏭</span>
                  <span>
                    Industry standard software = industry standard results
                  </span>
                </li>
                <li>
                  <span className="list-icon">🔄</span>
                  <span>
                    Same features = same capabilities = same limitations
                  </span>
                </li>
                <li>
                  <span className="list-icon">⚖️</span>
                  <span>No operational advantage over competitors</span>
                </li>
                <li>
                  <span className="list-icon">💰</span>
                  <span>
                    Competing on price because you can't compete on efficiency
                  </span>
                </li>
              </ul>
            </div>

            <div className="impact-card">
              <h3>Why This Matters:</h3>
              <p className="impact-text">
                If you and your competitors use identical tools, you'll get
                identical results. Competitive advantage comes from doing things
                differently and better.
              </p>
              <div className="stat-box">
                <span className="stat-number">68%</span>
                <span className="stat-unit">
                  Higher margins for differentiated businesses
                </span>
              </div>
            </div>

            <div className="solution-card">
              <div className="solution-header">
                <span className="solution-icon">🛡️</span>
                <h3>The Custom Solution:</h3>
              </div>
              <p className="solution-text">
                Custom software becomes your secret weapon. Processes your
                competitors can't replicate. Efficiency they can't match.
                Features they don't have.
              </p>
            </div>
          </div>
        </div>
      ),
    },

    // Slide 7: Conclusion
    {
      id: "conclusion",
      content: (
        <div className="content-slide">
          <div className="slide-header">
            <div className="slide-number">🎯</div>
            <h2 className="slide-title">
              If You Recognized 2+ Signs, It's Time To Explore Custom Business
              Software
            </h2>
          </div>

          <div className="slide-content">
            <div className="summary-card">
              <h3>The Reality Check:</h3>
              <div className="reality-list">
                <div className="reality-item">
                  <span className="reality-icon">📦</span>
                  <div className="reality-content">
                    <strong>Off-the-shelf software</strong> isn't bad it's just
                    not built for YOU
                  </div>
                </div>
                <div className="reality-item">
                  <span className="reality-icon">💎</span>
                  <div className="reality-content">
                    <strong>Custom software</strong> isn't a luxury it's a
                    strategic investment
                  </div>
                </div>
                <div className="reality-item">
                  <span className="reality-icon">⏳</span>
                  <div className="reality-content">
                    The cost of staying with inadequate software compounds daily
                  </div>
                </div>
              </div>
            </div>

            <div className="benefits-grid">
              <div className="benefit-card">
                <div className="benefit-icon">🎯</div>
                <h4>Exact Fit</h4>
                <p>Built for your workflow, not the other way around</p>
              </div>
              <div className="benefit-card">
                <div className="benefit-icon">👑</div>
                <h4>Ownership</h4>
                <p>You own it. No subscriptions, no hostages</p>
              </div>
              <div className="benefit-card">
                <div className="benefit-icon">⚔️</div>
                <h4>Competitive Edge</h4>
                <p>Capabilities competitors don't have</p>
              </div>
              <div className="benefit-card">
                <div className="benefit-icon">📈</div>
                <h4>Scalability</h4>
                <p>Grows exactly how your business grows</p>
              </div>
              <div className="benefit-card">
                <div className="benefit-icon">💰</div>
                <h4>Real ROI</h4>
                <p>Investment that pays back, not endless rental</p>
              </div>
            </div>

            <div className="final-cta">
              <h2 className="cta-title">
                The Question Isn't{" "}
                <span className="highlight">
                  "Can I Afford Custom Business Software?"
                </span>
              </h2>
              <h2 className="cta-title">
                The Question Is{" "}
                <span className="highlight-success">
                  "Can I Afford NOT To Have It?"
                </span>
              </h2>
            </div>
          </div>
        </div>
      ),
    },

    // Slide 8: Action
    {
      id: "action",
      content: (
        <div className="content-slide">
          <div className="slide-header">
            <div className="slide-number">🚀</div>
            <h2 className="slide-title">
              Ready To Explore What Custom Could Do For Your Business?
            </h2>
          </div>

          <div className="slide-content">
            <div className="action-hero">
              <h2 className="action-title">
                Let's Have An Honest Conversation
              </h2>
              <p className="action-subtitle">
                No pushy sales pitch. No generic demo. Just a real discussion
                about what's possible.
              </p>
            </div>

            <div className="discussion-grid">
              <div className="discussion-item">
                <div className="discussion-icon">🎯</div>
                <h4>Your Specific Challenges</h4>
                <p>We listen first, understand your pain points</p>
              </div>
              <div className="discussion-item">
                <div className="discussion-icon">💡</div>
                <h4>What's Possible</h4>
                <p>Explore real solutions tailored to your business</p>
              </div>
              <div className="discussion-item">
                <div className="discussion-icon">📅</div>
                <h4>Realistic Timeline</h4>
                <p>Clear milestones and delivery expectations</p>
              </div>
              <div className="discussion-item">
                <div className="discussion-icon">💰</div>
                <h4>Transparent Costs</h4>
                <p>No hidden fees, just honest pricing</p>
              </div>
            </div>

            <div className="action-buttons">
              <a
                href="https://topmate.io/pluralcode/1677658"
                className="action-btn primary-btn"
                target="_blank"
                rel="noopener noreferrer"
              >
                <span className="btn-icon">📅</span>
                <span className="btn-text">Schedule Free Consultation</span>
                <span className="btn-arrow">→</span>
              </a>

              <div className="contact-buttons">
                <a
                  href="mailto:info@pluralcode.com"
                  className="action-btn secondary-btn"
                >
                  <span className="btn-icon">📧</span>
                  <span className="btn-text">Email Us</span>
                </a>

                <a
                  href="https://wa.me/919958920231?text=Hi%2C%20I%20want%20to%20discuss%20custom%20business%20software%20for%20my%20business."
                  className="action-btn whatsapp-btn"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <span className="btn-icon">💬</span>
                  <span className="btn-text">WhatsApp Chat</span>
                </a>
              </div>
            </div>

            <div className="guarantee-box">
              <div className="guarantee-icon">🎯</div>
              <div className="guarantee-content">
                <h4>What You Get:</h4>
                <p>
                  <strong>
                    60-minute strategy session • No-obligation • Actionable
                    insights • ROI analysis
                  </strong>
                </p>
              </div>
            </div>
          </div>
        </div>
      ),
    },
  ];
  const { slug: paramSlug } = useParams();
  const location = useLocation();
  const navigate = useNavigate();




  const fallbackSlugMap = {
    "/lm/five-signs-you-need-custom-business-software":
      "five-signs-you-need-custom-business-software",
  };

  const actualSlug = paramSlug || fallbackSlugMap[location.pathname] || "";

  const searchParams = new URLSearchParams(location.search);


  const isDnscp = searchParams.get("dnscp") === "true";

  const utmParams = {
    utm_source: searchParams.get("utm_source") || "direct",
    utm_medium: searchParams.get("utm_medium") || "email",
    utm_campaign: searchParams.get("utm_campaign") || "default",
  };

  const {
    apiData: leadMagnet,
    isLoading: isLeadMagnetLoading,
    error: leadMagnetError,
  } = useFetch(() => getLeadMagnet(actualSlug, utmParams), actualSlug);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
  });

  const [showError, setShowError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);
  // const [showModal, setShowModal] = useState(true);
  // const [showModal, setShowModal] = useState(!isDnscp);
    const [showModal, setShowModal] = useState(false);
     const [hasModalBeenShown, setHasModalBeenShown] = useState(false);

  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    if (leadMagnetError) {
      cogoToast.error("Failed to load content");
    }
    if (leadMagnet?.status === "error") {
      cogoToast.error("Failed to load content");
    }
  }, [leadMagnetError, leadMagnet]);

    useEffect(() => {
    if (currentSlide === 5 && !hasModalBeenShown && !isFormSubmitted && !isDnscp) {
      setShowModal(true);
      setHasModalBeenShown(true);
    }
  }, [currentSlide, hasModalBeenShown, isFormSubmitted, isDnscp]);


  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (showError) setShowError(false);
  };

  const submitLeadForm = async () => {
    const { name, email, phone } = formData;

    // Validation
    if (!name.trim() || !email.trim()) {
      setShowError(true);
      cogoToast.info("Please fill in all required fields");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setShowError(true);
      cogoToast.error("Please enter a valid email address");
      return;
    }

    if (phone.trim()) {
      const phoneRegex = /^\d{10}$/;
      if (!phoneRegex.test(phone)) {
        setShowError(true);
        cogoToast.error("Phone number must be exactly 10 digits");
        return;
      }
    }

    setIsSubmitting(true);

    try {
      const payload = {
        name,
        email,
        phone: phone || null,
        slug: leadMagnet?.slug || actualSlug,
        offering_type: leadMagnet?.offering_type || "lead_magnet",
      };

      const response = await postLeadMagnetResponseForm(payload);

      if (response.status === "ok") {
        cogoToast.success("Thank you! You can now access the content.");
        setFormData({
          name: "",
          email: "",
          phone: "",
        });
        setShowError(false);
        setIsFormSubmitted(true);
        setShowModal(false);
      } else {
        cogoToast.error(
          response.message || "Something went wrong. Please try again."
        );
      }
    } catch (err) {
      console.error("Form submission error:", err);
      cogoToast.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // const handleCancel = () => {
  //   navigate("/");
  // };

    const handleCancel = () => {
    setShowModal(false); // Just close the modal, don't navigate away
  };

  const goToNext = () => {
    if (currentSlide < slides.length - 1) {
      setCurrentSlide(currentSlide + 1);
    }
  };

  const goToPrev = () => {
    if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }
  };

  const progressPercentage = (currentSlide / (slides.length - 1)) * 100;

  return (
    <div className="lead-magnet-container">
      {/* Modal */}
      {showModal && !isFormSubmitted && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header" style={{ flexDirection: "column" }}>
              <div className="modal-badge">
                <span className="badge-icon">📊</span>
                <span className="badge-text">FREE GUIDE</span>
              </div>
              <h2 className="modal-title">Get Instant Access</h2>
              <p className="modal-subtitle">
                Enter your details to unlock the complete guide to custom
                business software
              </p>
            </div>

            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">
                  <span className="label-text">Full Name</span>
                  <span className="required">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="form-input"
                  placeholder="Enter your full name"
                />
              </div>

              <div className="form-group">
                <label className="form-label">
                  <span className="label-text">Email Address</span>
                  <span className="required">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="form-input"
                  placeholder="Enter your email address"
                />
              </div>

              <div className="form-group">
                <label className="form-label">
                  <span className="label-text">Phone Number</span>
                  <span className="optional">(Optional)</span>
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="form-input"
                  placeholder="Enter 10-digit phone number"
                  maxLength="10"
                />
                <small className="form-help">10 digits only</small>
              </div>

              {showError && (
                <div className="error-message">
                  <span className="error-icon">⚠️</span>
                  Please fill all required fields correctly
                </div>
              )}

              <div className="form-actions">
                <button
                  onClick={handleCancel}
                  className="btn btn-secondary"
                  disabled={isSubmitting}
                >
                  Close
                </button>
                <button
                  onClick={submitLeadForm}
                  className="btn btn-primary"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <span className="spinner"></span>
                      Processing...
                    </>
                  ) : (
                    <>
                      <span className="btn-icon">🔓</span>
                      Unlock The Guide
                    </>
                  )}
                </button>
              </div>

              <div className="privacy-note">
                <span className="privacy-icon">🔒</span>
                <span>
                  Your information is secure. No spam. Unsubscribe anytime.
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="lead-magnet-wrapper">
        {/* Progress Bar */}
        <div className="progress-container">
          <div
            className="progress-bar"
            style={{ width: `${progressPercentage}%` }}
          />
          <div className="progress-text">
            Slide {currentSlide + 1} of {slides.length}
          </div>
        </div>

        {/* Current Slide */}
        <div className="slide-container">{slides[currentSlide].content}</div>

        {/* Navigation */}
        <div className="navigation-container">
          <button
            onClick={goToPrev}
            className="nav-btn prev-btn"
            disabled={currentSlide === 0}
          >
            <span className="nav-icon">←</span>
            <span className="nav-text">Previous</span>
          </button>

          <div className="slide-indicators">
            {slides.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentSlide(index)}
                className={`slide-indicator ${
                  index === currentSlide ? "active" : ""
                }`}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>

          <button
            onClick={goToNext}
            className="nav-btn next-btn"
            disabled={currentSlide === slides.length - 1}
          >
            <span className="nav-text">
              {currentSlide === slides.length - 1 ? "Finish" : "Next"}
            </span>
            <span className="nav-icon">→</span>
          </button>
        </div>
      </div>
      <FiveSignsYouNeedCustomBusinessSoftwareStyles />
    </div>
  );
};

export default FiveSignsYouNeedCustomBusinessSoftware;
